import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Pagination from "../../../components/Pagination";
import Table from "../../../components/table";
import usePagination from "../../../components/usePagination";
import BackButton from "../../../components/BackButton";
import api from "../../../api/api";
import * as bootstrap from "bootstrap";
import "../../../pages/Freight/Common/form.css";
import SearchIcon from "@mui/icons-material/Search";

const HighVolumeCustomers = () => {
  const location = useLocation();
  const { fromDate, toDate, createdBy } = location.state || {};

  console.log('HighVolumeCustomers - Received filters:', { fromDate, toDate, createdBy });

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  /* ================= FETCH HIGH VOLUME CUSTOMERS ================= */
  useEffect(() => {
    const fetchCustomers = async () => {
      setLoading(true);
      console.log('Fetching high volume customers with params:', { fromDate, toDate, createdBy });
      try {
        const params = {};
        if (fromDate) params.fromDate = fromDate;
        if (toDate) params.toDate = toDate;
        if (createdBy) params.CreateBY = createdBy;
        console.log('API params:', params);

        const response = await api.get("/sales/high-volume-customers-list", { params });
        console.log('API response:', response.data);

        setCustomers(response.data.customers || []);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch customers");
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, [fromDate, toDate, createdBy]);

  /* ================= SEARCH FILTER ================= */
  const filteredCustomers = customers.filter((customer) => {
    const term = search.toLowerCase();

    return (
      customer.first_name?.toLowerCase().includes(term) ||
      customer.last_name?.toLowerCase().includes(term) ||
      customer.company_name?.toLowerCase().includes(term)
    );
  });

  /* ================= PAGINATION ================= */
  const {
    currentPage,
    itemsPerPage,
    totalPages,
    totalItems,
    currentItems,
    goToPage,
    handleItemsPerPageChange,
  } = usePagination(filteredCustomers, 5);

  /* ================= TOOLTIP ================= */
  useEffect(() => {
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]'),
    );
    tooltipTriggerList.forEach((el) => new bootstrap.Tooltip(el));
  }, [filteredCustomers, currentPage]);

  if (error) return <p className="text-danger">{error}</p>;

  /* ================= TABLE COLUMNS ================= */
  const columns = [
    {
      label: "Client Name",
      render: (customer) => (
        <span
          data-bs-toggle="tooltip"
          title={`${customer.first_name || ""} ${customer.last_name || ""}`}
        >
          {customer.first_name} {customer.last_name}
        </span>
      ),
    },
    {
      label: "Company Name",
      render: (customer) => (
        <span data-bs-toggle="tooltip" title={customer.company_name || "-"}>
          {customer.company_name || "-"}
        </span>
      ),
    },
    {
      label: "Estimated Monthly Volume",
      render: (customer) => (
        <span data-bs-toggle="tooltip" title={customer.estimated_monthly_volume?.toString() || "N/A"}>
          {customer.estimated_monthly_volume || "N/A"}
        </span>
      ),
    },
    {
      label: "Handled By",
      render: (customer) => {
        let name = customer.created_by;

        if (!name) {
          name = "N/A";
        } else {
          // Convert anything to string
          name = String(name).trim();

          // Case 1: comma-separated duplicates
          if (name.includes(",")) {
            const parts = name
              .split(",")
              .map((p) => p.trim())
              .filter(Boolean);

            // If all names are same, keep one
            if (new Set(parts).size === 1) {
              name = parts[0];
            }
          }

          // Case 2: concatenated duplicates (no separator)
          const half = name.length / 2;
          if (
            name.length % 2 === 0 &&
            name.slice(0, half) === name.slice(half)
          ) {
            name = name.slice(0, half);
          }
        }

        return (
          <span data-bs-toggle="tooltip" title={name}>
            {name}
          </span>
        );
      },
    },
    {
      label: "Created Date",
      render: (customer) => {
        if (!customer.created_at) return "-";
        const date = new Date(customer.created_at);
        const dateStr = date.toISOString().split("T")[0];
        const timeStr = date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
        return `${dateStr} ${timeStr}`;
      },
    },
  ];

  /* ================= RENDER ================= */
  return (
    <>
      <BackButton />

      {/* SEARCH INPUT */}
      <div className="mb-3 position-relative" style={{ maxWidth: "100%" }}>
        <input
          type="search"
          className="form-control ps-5"
          placeholder="Search by Client Name or Company Name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
 
        <SearchIcon
          style={{
            position: "absolute",
            left: "12px",
            top: "50%",
            transform: "translateY(-50%)",
            cursor: "pointer",
            color: "#6b7280",
            width:"16px",
            height:"16px"
          }}
        />
      </div>

      {/* TABLE */}
      <Table
        columns={columns}
        data={currentItems}
        loading={loading}
        emptyMessage="No High Volume Customers found."
        itemsPerPage={itemsPerPage}
        pagination={
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalItems={totalItems}
          />
        }
      />
    </>
  );
};

export default HighVolumeCustomers;
